import { t } from 'i18next';
import { cn } from '../../../lib/styles';
import { Heading } from '../heading/heading.spark';
import { Icon } from '../icon/icon.spark';
import type { IconName } from '../icon/types';
import type { AlertProps, AlertVariant } from './types';

const ICON_NAMES: Record<AlertVariant, IconName> = {
  error: 'xCircle',
  warning: 'alertTriangle',
  info: 'infoCircle',
  success: 'checkCircle',
} as const;

export function Alert({ variant = 'info', ...props }: AlertProps) {
  const headerStyle = cn(
    'flex px-3 py-2 text-white',
    props.title ? 'rounded-t-lg' : 'rounded-s-lg',
    {
      'bg-red-400': variant === 'error',
      'bg-yellow-400': variant === 'warning',
      'bg-blue-400': variant === 'info',
      'bg-green-400 ring-offset-blue-500': variant === 'success',
    },
  );

  const bodyStyle = cn(
    'flex flex-1 items-center overflow-hidden border p-3',
    props.title ? 'rounded-b-lg' : 'rounded-e-lg',
    {
      'border-red-400 bg-red-50 text-red-800': variant === 'error',
      'border-yellow-400 bg-yellow-50 text-yellow-800': variant === 'warning',
      'border-blue-400 bg-blue-50 text-blue-800': variant === 'info',
      'border-green-400 bg-green-50 text-green-800': variant === 'success',
    },
  );

  const closeButtonStyle = cn('rounded-md p-0.5 outline-hidden focus:ring-2 focus:ring-offset-2', {
    'ring-red-500 ring-offset-red-400 hover:bg-red-500': variant === 'error',
    'ring-yellow-600 ring-offset-yellow-400 hover:bg-yellow-500': variant === 'warning',
    'ring-blue-600 ring-offset-blue-400 hover:bg-blue-500': variant === 'info',
    'ring-green-600 ring-offset-green-400 hover:bg-green-500': variant === 'success',
  });

  // Alert will interrupt screen reader to announce. Status will wait for pause in screen reader
  const ariaRole = variant === 'error' ? 'alert' : 'status';

  return (
    <div
      role={ariaRole}
      className={cn('flex w-full rounded-lg text-sm shadow', { 'flex-col': props.title })}
    >
      <div className={cn(headerStyle, 'flex items-center justify-between')}>
        <Heading
          icon={ICON_NAMES[variant]}
          iconSize="lg"
          level={props.headingLevel}
          style="sm"
          colour="white"
          weight="normal"
        >
          {props.title}
        </Heading>
        {props.title && props.onDismiss && (
          <button
            type="button"
            className={closeButtonStyle}
            onClick={props.onDismiss}
            aria-label={t('aria:dismiss')}
          >
            <Icon name="x" />
          </button>
        )}
      </div>
      <div className={bodyStyle}>{props.children}</div>
    </div>
  );
}
